import { useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { Loader2, KeyRound } from "lucide-react"
import { authApi, ApiError } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

type Props = {
  challengeId: string
  email: string
  onSuccess: (csrf: string) => void
  onBack: () => void
}

export function TotpChallenge({ challengeId, email, onSuccess, onBack }: Props) {
  const [code, setCode] = useState("")
  const [showRecovery, setShowRecovery] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const codeInputRef = useRef<HTMLInputElement>(null)

  const verify = useMutation({
    mutationFn: () => authApi.verify2fa(challengeId, code),
    onSuccess: (data) => onSuccess(data.csrf),
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Verification failed")
      setCode("")
      codeInputRef.current?.focus()
    },
  })

  const verifyRecovery = useMutation({
    mutationFn: () => authApi.verifyRecovery(challengeId, recoveryCode),
    onSuccess: (data) => onSuccess(data.csrf),
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Invalid recovery code")
    },
  })

  return (
    <>
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-rc-blue-500/20 text-rc-blue-300 ring-1 ring-rc-blue-400/30">
          <KeyRound className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-semibold text-white">Two-factor authentication</h2>
        <p className="mt-1 text-sm text-white/60">
          {showRecovery
            ? "Enter one of your 12-digit recovery codes."
            : `Enter the 6-digit code from your authenticator app for ${email}.`}
        </p>
      </div>

      {!showRecovery ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            verify.mutate()
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="code">Authenticator code</Label>
            <Input
              id="code"
              ref={codeInputRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="text-center font-mono text-2xl tracking-[0.5em]"
              required
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
            disabled={verify.isPending || code.length !== 6}
          >
            {verify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign In"}
          </Button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            verifyRecovery.mutate()
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="recovery">Recovery code</Label>
            <Input
              id="recovery"
              placeholder="0000-0000-0000"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              required
              autoFocus
              className="text-center font-mono tracking-widest"
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
            disabled={verifyRecovery.isPending}
          >
            {verifyRecovery.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
          </Button>
        </form>
      )}

      <div className="mt-6 flex flex-col gap-3 text-center text-sm">
        <button
          type="button"
          onClick={() => {
            setShowRecovery(!showRecovery)
            setError(null)
          }}
          className="text-rc-blue-300 transition-colors hover:text-rc-blue-200 hover:underline"
        >
          {showRecovery ? "Use authenticator code instead" : "Use a recovery code"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="text-white/50 transition-colors hover:text-white"
        >
          ← Start over
        </button>
      </div>
    </>
  )
}
